import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Template} from "../../../../components/template/template.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaCog, FaEraser, FaSearch} from "react-icons/fa";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import SelectComponent from "../../../../components/select/select-component.jsx";
import {useQuery} from "@tanstack/react-query";
import NocService from "../../../../services/web/noc-service.js";
import StatusCellRenderer from "../../noc-cameras/components/status-cell-renderer.jsx";
import {navigateHook} from "../../../../hooks/use-navigate.js";

const API_ARCHIVER = import.meta.env.VITE_API_ARCHIVER;

const NocSmartcapturesList = () => {
    const gridRef = useRef(null);
    const eventSourceRef = useRef(null);

    const [rowData, setRowData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [filters, setFilters] = useState([]);
    const [inputs, setInputs] = useState({
        archiver: 1,
        maintenanceGroup: "",
    });

    const navigate = navigateHook();

    const theme = useMemo(() => aggridUtils.generalTheme(), []);

    const { data: archivers } = useQuery({
        queryKey: ["archivers"],
        queryFn: () =>
            NocService.allArchivers().then((data) =>
                data.map((item) => ({
                    value: item.id,
                    label: String(item.text),
                }))
            ),
    });

    const statusComparator = (a, b, nodeA, nodeB) => {
        const getSortStatus = (status) => {
            if (status === "OFFLINE") return 1;
            if (status === "WARNING") return 2;
            return 3;
        };
        return getSortStatus(nodeA.data.status) - getSortStatus(nodeB.data.status);
    };

    const openSmartcapture = (pin) => {
        let isBlank = false;
        const urlDev = `/smartcaptures/form/${pin}`;
        
        navigate(urlDev, isBlank);
    };

    const openNocCameras = (cameras) => {
        let isBlank = true;

        const lanesArr = String(cameras).split(",").map(e => e.trim()).filter(Boolean);
        const filterNocCamera = { field: "code", type: "in", value: lanesArr};

        const filterStorage = localStorage.getItem("noc-camera-filter");
        const newFilterToStorage = [];

        if (filterStorage) {
            const filterParsed = JSON.parse(filterStorage);

            for (let i = 0; i < filterParsed.length; i++) {
                if (filterParsed[i].field !== "code") {
                    newFilterToStorage.push(filterParsed[i]);
                }
            }
        }

        newFilterToStorage.push(filterNocCamera);

        localStorage.setItem("noc-camera-filter", JSON.stringify(newFilterToStorage));

        const urlDev = `/noc-cameras`;
        
        navigate(urlDev, isBlank);
    };

    const defaultColDef = useMemo(() => ({
        tooltipValueGetter: (params) => {
            if (params.value === null || params.value === undefined) return "";
            return String(params.value);
        },
        sortable: true,
        resizable: true,
    }), []);

    const columnDefs = [
        { headerName: "", field: "status", cellClass: "!p-0 !flex justify-center items-center", width: 40, sort: 'asc',
            comparator: statusComparator,
            cellRenderer: StatusCellRenderer,
        },
        { headerName: "Dispositivo", headerTooltip: "Dispositivo", field: "name", flex: 1 },
        { headerName: "IP detectado", field: "ipServer", width: 120 },
        { headerName: "Acesso remoto", field: "remoteAccess", width: 120 },
        { headerName: "PIN", field: "PIN", width: 80 },
        { headerName: "Responsável", field: "maintenanceGroup", width: 120 },
        { headerName: "Câmeras", field: "lanes", width: 120,
            cellRenderer: (params) => {
                return (
                    <span
                        className="flex items-center justify-center cursor-pointer hover:underline w-full h-full"
                        onClick={() => openNocCameras(params.value)}
                    >
                        {params.value}
                    </span>
                );
            },
        },
        { headerName: "Versão", field: "version", width: 120 },
        { headerName: "Passagens pendentes", field: "pendingPassages", width: 175 },
        { headerName: "Detalhes", field: "details", width: 175 },
        { headerName: "", field: "config", width: 50,
            cellRenderer: (params) => (
                <div className="flex justify-center items-center w-full h-full">
                    <Button
                        target="primary"
                        className="h-[32px] w-[32px] !p-0"
                        onClick={() => openSmartcapture(params.data.PIN)}
                        title={"Configurar smartcapture"}
                    >
                        <FaCog />
                    </Button>
                </div>
            ),
        },
    ];

    const connectSSE = useCallback(() => {
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
        }

        const source = new EventSource(
            `${API_ARCHIVER}/${inputs.archiver}/sse/noc/smartcapture/subscribe`
        );

        source.onmessage = (e) => {
            const data = JSON.parse(e.data);
            setRowData(data.value || []);
        };

        source.onerror = (e) => console.error(e);

        eventSourceRef.current = source;
    }, [inputs.archiver]);

    useEffect(() => {
        connectSSE();
        return () => eventSourceRef.current?.close();
    }, [connectSSE]);

    const applyFilter = useCallback(() => {
        let data = [...rowData];

        filters.forEach((f) => {
            if (f.type === "like") {
                data = data.filter((row) =>
                    String(row[f.field] ?? "")
                        .toLowerCase()
                        .includes(f.value.toLowerCase())
                );
            }
        });

        setFilteredData(data);
    }, [rowData, filters]);

    useEffect(() => {
        applyFilter();
    }, [rowData, filters]);

    const handleSearch = () => {
        const newFilters = [];

        if (inputs.maintenanceGroup) {
            newFilters.push({
                field: "maintenanceGroup",
                type: "like",
                value: inputs.maintenanceGroup,
            });
        }

        setFilters(newFilters);
    };

    const clearFilter = () => {
        setInputs((prev) => ({
            ...prev,
            maintenanceGroup: "",
        }));

        setFilters([]);
        setFilteredData(rowData);
    };

    useEffect(() => {
        const handler = (e) => {
            if (e.key === "Enter") handleSearch();
            if (e.key === "Escape") clearFilter();
        };

        window.addEventListener("keyup", handler);
        return () => window.removeEventListener("keyup", handler);
    }, [inputs]);

    return (
        <Template>
            <Template.Header>
                <Template.Title>NOC Smartcaptures</Template.Title>
            </Template.Header>

            <Template.Body>
                <Card>
                    <Card.Body>
                        <div className="grid grid-cols-12 gap-4">
                            <SelectComponent
                                label="Archiver"
                                value={archivers?.find(a => a.value === inputs.archiver)}
                                options={archivers || []}
                                onChange={(selectedOption) =>
                                    setInputs((prev) => ({
                                        ...prev,
                                        archiver: selectedOption?.value ?? null,
                                    }))
                                }
                                isClearable={false}
                                classNameWrapper="col-span-2"
                            />

                            <div className="col-span-10 flex gap-4 items-end">
                                <InputForm
                                    id={"responsible"}
                                    label="Nome do responsável"
                                    value={inputs.maintenanceGroup}
                                    onChange={(e) =>
                                        setInputs({
                                            ...inputs,
                                            maintenanceGroup: e.target.value,
                                        })
                                    }
                                    classNameWrapper="flex-1"
                                />

                                <div className={"flex gap-2"}>
                                    <Button target="primary-outline" title={"Limpar"} onClick={clearFilter}>
                                        <FaEraser />
                                    </Button>

                                    <Button target="primary" title={"Buscar"} onClick={handleSearch}>
                                        <FaSearch />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                <Card className="mt-2 h-[calc(100vh-131px)]">
                    <Card.Body className="h-full table-smartcapture">
                        <AgGridReact
                            ref={gridRef}
                            rowData={filteredData}
                            columnDefs={columnDefs}
                            defaultColDef={defaultColDef}
                            tooltipShowDelay={200}
                            suppressCellFocus
                            gridOptions={{ localeText: aggridUtils.localization() }}
                            rowHeight={40}
                            theme={theme}
                            animateRows={false}
                            getRowId={(params) => String(params.data.PIN)}
                            enableCellTextSelection={true}
                            ensureDomOrder={true}
                        />
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default NocSmartcapturesList;
